import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/api/client';
import type { Vacancy, VacancyStats } from '@/types';

interface VacancyListProps {
  selectedId: string | null;
  onSelect: (id: string) => void;
}

const statusLabels: Record<string, string> = {
  draft: 'Черновик',
  active: 'Активна',
  closed: 'Закрыта',
};

const statusColors: Record<string, string> = {
  draft: 'bg-secondary-100 text-secondary-700',
  active: 'bg-accent-100 text-accent-700',
  closed: 'bg-background-200 text-foreground-500',
};

export default function VacancyList({ selectedId, onSelect }: VacancyListProps) {
  const { orgId } = useAuth();
  const [vacancies, setVacancies] = useState<Vacancy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    setLoading(true);
    api.get<Vacancy[]>('/vacancies')
      .then((data) => {
        if (!cancelled) {
          setVacancies(data);
          if (data.length > 0 && !selectedId) {
            onSelect(data[0].id);
          }
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Ошибка загрузки');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [orgId, selectedId, onSelect]);

  if (loading) {
    return (
      <div className="p-6 space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-20 rounded-xl bg-background-100 animate-pulse"></div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="p-4 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm">
          {error}
        </div>
      </div>
    );
  }

  if (vacancies.length === 0) {
    return (
      <div className="p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
          <i className="ri-briefcase-line text-xl text-foreground-400"></i>
        </div>
        <p className="text-sm text-foreground-500">Нет вакансий</p>
      </div>
    );
  }

  return (
    <div className="py-3 px-3 space-y-1">
      {vacancies.map((v) => (
        <button
          key={v.id}
          onClick={() => onSelect(v.id)}
          className={`w-full text-left p-4 rounded-xl transition-all cursor-pointer border ${
            selectedId === v.id
              ? 'bg-white border-primary-300 ring-1 ring-primary-300'
              : 'bg-transparent border-transparent hover:bg-background-100'
          }`}
        >
          <div className="flex items-start justify-between gap-2 mb-2">
            <h3 className="font-medium text-sm text-foreground-900 leading-snug line-clamp-2">{v.title}</h3>
            <span className={`shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-full ${statusColors[v.status] || statusColors.draft}`}>
              {statusLabels[v.status] || v.status}
            </span>
          </div>
          <p className="text-xs text-foreground-500 mb-2">{v.company_name}{v.location ? ` · ${v.location}` : ''}</p>
          <div className="flex items-center gap-3 text-[11px] text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-user-line"></i>
              Кандидаты
            </span>
            {v.hh_vacancy_id && (
              <span className="flex items-center gap-1 text-accent-600">
                <i className="ri-global-line"></i>
                hh.ru
              </span>
            )}
          </div>
        </button>
      ))}
    </div>
  );
}